import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useParams, useNavigate } from "react-router-dom";
import { StatusBadge } from "./Dashboard";
import { toast } from "sonner";
import { CheckCircle, FloppyDisk, Plus, Trash, ArrowLeft, Database } from "@phosphor-icons/react";
import { PushToErpDialog } from "../components/PushToErpDialog";
import { CustomerSelect } from "../components/CustomerSelect";

export default function PODetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [po, setPo] = useState(null);
  const [events, setEvents] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [tab, setTab] = useState("header");
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [showPush, setShowPush] = useState(false);

  const load = () => {
    api.get(`/po/${id}`).then((r) => setPo(r.data));
    api.get(`/po/${id}/events`).then((r) => setEvents(r.data));
    api.get(`/po/${id}/siblings`).then((r) => setSiblings(r.data)).catch(() => setSiblings([]));
  };
  useEffect(() => { load(); }, [id]);

  if (!po) return <Layout breadcrumb={["Open Orders", "Loading…"]}><div>Loading…</div></Layout>;

  const set = (k, v) => { setPo({ ...po, [k]: v }); setDirty(true); };
  const setLine = (i, k, v) => {
    const lines = [...po.line_items];
    lines[i] = { ...lines[i], [k]: v };
    setPo({ ...po, line_items: lines });
    setDirty(true);
  };
  const addLine = () => {
    setPo({ ...po, line_items: [...po.line_items, { sku: "", rev: "", description: "", quantity_ordered: 0, quantity_shipped: 0, unit_price: null, uom: "", due_date: "" }] });
    setDirty(true);
  };
  const removeLine = (i) => {
    const lines = [...po.line_items];
    lines.splice(i, 1);
    setPo({ ...po, line_items: lines });
    setDirty(true);
  };

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        po_number: po.po_number, customer_name: po.customer_name, customer_email: po.customer_email,
        portal_pcode: po.portal_pcode || null,
        order_date: po.order_date, requested_ship_date: po.requested_ship_date,
        ship_to_address: po.ship_to_address, bill_to_address: po.bill_to_address,
        currency: po.currency, total_amount: po.total_amount, notes: po.notes,
        line_items: po.line_items.map((li) => ({
          sku: li.sku, rev: li.rev || null, description: li.description,
          quantity_ordered: Number(li.quantity_ordered) || 0,
          quantity_shipped: Number(li.quantity_shipped) || 0,
          unit_price: li.unit_price === "" || li.unit_price === null ? null : Number(li.unit_price),
          uom: li.uom, due_date: li.due_date || null,
        })),
      };
      await api.put(`/po/${id}`, payload);
      toast.success("PO saved");
      setDirty(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    if (dirty) await save();
    try {
      await api.post(`/po/${id}/confirm`);
      toast.success("PO confirmed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout breadcrumb={["Open Orders", po.po_number || "PO"]}>
      <div className="jde-toolbar">
        <button className="jde-btn jde-btn-sm" onClick={() => nav("/orders")} data-testid="po-back">
          <ArrowLeft size={12} weight="bold" /> Back
        </button>
        <div className="divider" />
        <span style={{ fontSize: 13, fontWeight: 700 }}>PO {po.po_number || "(unnumbered)"}</span>
        <StatusBadge status={po.status} />
        {po.is_change_order && (
          <span className="jde-badge warning" data-testid="po-change-badge"
                title={`Change / revision PO — markers: ${(po.change_markers || []).join(", ")}`}>
            CHANGE PO
          </span>
        )}
        {po.is_change_order && po.change_of_po_id && (
          <button className="jde-btn jde-btn-sm" data-testid="po-view-original"
                  onClick={() => nav(`/po/${po.change_of_po_id}`)}
                  title="Open the original PO this change revises">
            View original ↗
          </button>
        )}
        {po.is_change_order && !po.change_of_po_id && po.matches_sap_po && (
          <span className="jde-badge alert" data-testid="po-sap-match-warn"
                title="A matching PO exists in the SAP/Excel data (read-only). Reconcile this change manually.">
            Matches SAP PO — review
          </span>
        )}
        {po.is_silent_revision && (
          <span className="jde-badge alert" data-testid="po-silent-revision-badge"
                title="This PO arrived with NO revision keyword but its content differs from an earlier copy — treated as a silent revision.">
            SILENT REVISION
          </span>
        )}
        {po.superseded && (
          <span className="jde-badge alert" data-testid="po-superseded-badge"
                title="A newer revision of this PO was received. This record is excluded from open-order counts.">
            SUPERSEDED
          </span>
        )}
        {po.superseded && po.superseded_by && (
          <button className="jde-btn jde-btn-sm" data-testid="po-view-latest"
                  onClick={() => nav(`/po/${po.superseded_by}`)}
                  title="Open the latest revision that superseded this record">
            View latest ↗
          </button>
        )}
        {po.confidence != null && (
          <span className="jde-badge confirmed">
            AI Confidence: {(po.confidence * 100).toFixed(0)}%
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button className="jde-btn" onClick={save} disabled={!dirty || loading} data-testid="po-save">
          <FloppyDisk size={12} weight="bold" /> Save
        </button>
        {po.status === "pending_review" && (
          <button className="jde-btn jde-btn-primary" onClick={confirm} data-testid="po-confirm">
            <CheckCircle size={12} weight="bold" /> Confirm Extraction
          </button>
        )}
        {po.status === "confirmed" && (po.source_type || "").indexOf("excel") === -1 && (
          <button className="jde-btn jde-btn-primary" onClick={() => setShowPush(true)} data-testid="po-push-erp">
            <Database size={12} weight="bold" /> Push to MS SQL
          </button>
        )}
        {po.status === "pushed_to_erp" && (
          <span className="jde-badge confirmed" data-testid="po-pushed-badge" style={{ background: "#DCFCE7", color: "#166534" }}>
            <Database size={11} weight="bold" style={{ verticalAlign: "-2px", marginRight: 3 }} />
            Pushed to ERP{po.ms_pono ? ` · PoNo ${po.ms_pono}` : ""}
          </span>
        )}
      </div>

      {showPush && (
        <PushToErpDialog poId={id} poNumber={po.po_number}
                         onClose={() => setShowPush(false)}
                         onDone={() => { load(); }} />
      )}

      {(po.revision_diff && po.revision_diff.length > 0) && (
        <div className="jde-panel" data-testid="po-revision-diff"
             style={{ marginTop: 12, border: "1px solid #d97706", background: "#fffbeb" }}>
          <div style={{ padding: "8px 12px", fontWeight: 700, fontSize: 13,
                        color: "#92400e", borderBottom: "1px solid #fcd34d" }}>
            {po.is_silent_revision ? "Silent revision" : "Revision"} — {po.revision_diff.length} field change(s) vs the record it supersedes
            {po.change_of_po_id && (
              <button className="jde-btn jde-btn-sm" data-testid="po-view-original-diff"
                      style={{ marginLeft: 10 }}
                      onClick={() => nav(`/po/${po.change_of_po_id}`)}>
                View superseded original ↗
              </button>
            )}
          </div>
          <table className="jde-table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Field</th>
                <th style={{ textAlign: "left" }}>Was</th>
                <th style={{ textAlign: "left" }}>Now</th>
              </tr>
            </thead>
            <tbody>
              {po.revision_diff.map((d, i) => (
                <tr key={i} data-testid={`po-revision-diff-row-${i}`}>
                  <td style={{ fontWeight: 600 }}>{d.field}</td>
                  <td style={{ color: "#b91c1c", textDecoration: "line-through" }}>{String(d.old ?? "—")}</td>
                  <td style={{ color: "#166534", fontWeight: 600 }}>{String(d.new ?? "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="jde-tabs" style={{ marginTop: 12 }}>
        <button className={`jde-tab ${tab === "header" ? "active" : ""}`} onClick={() => setTab("header")} data-testid="tab-header">
          Header Info
        </button>
        <button className={`jde-tab ${tab === "lines" ? "active" : ""}`} onClick={() => setTab("lines")} data-testid="tab-lines">
          Line Items ({po.line_items?.length || 0})
        </button>
        <button className={`jde-tab ${tab === "shipment" ? "active" : ""}`} onClick={() => setTab("shipment")} data-testid="tab-shipment">
          Shipment
        </button>
        <button className={`jde-tab ${tab === "timeline" ? "active" : ""}`} onClick={() => setTab("timeline")} data-testid="tab-timeline">
          Shipment Timeline
        </button>
        <button className={`jde-tab ${tab === "source" ? "active" : ""}`} onClick={() => setTab("source")} data-testid="tab-source">
          Source Document
        </button>
      </div>

      <div className="jde-panel" style={{ borderTop: 0 }}>
        {tab === "header" && (
          <div className="jde-panel-body" data-testid="header-form">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
              <Field label="PO Number" value={po.po_number} onChange={(v) => set("po_number", v)} testid="f-po" />
              <CustomerSelect name={po.customer_name} pcode={po.portal_pcode}
                              onChange={({ name, pcode }) => { setPo({ ...po, customer_name: name, portal_pcode: pcode }); setDirty(true); }}
                              testid="f-customer" />
              <Field label="Customer Email" value={po.customer_email} onChange={(v) => set("customer_email", v)} testid="f-cust-email" />
              <Field label="Order Date" value={po.order_date} onChange={(v) => set("order_date", v)} testid="f-order-date" />
              <Field label="Requested Ship Date" value={po.requested_ship_date} onChange={(v) => set("requested_ship_date", v)} testid="f-ship-date" />
              <Field label="Currency" value={po.currency} onChange={(v) => set("currency", v)} testid="f-currency" />
              <Field label="Total Amount" value={po.total_amount} onChange={(v) => set("total_amount", v)} type="number" testid="f-total" />
            </div>
            <div style={{ height: 10 }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label className="jde-label">Ship-To Address</label>
                <textarea className="jde-textarea" rows={3} value={po.ship_to_address || ""} onChange={(e) => set("ship_to_address", e.target.value)} data-testid="f-ship-to" />
              </div>
              <div>
                <label className="jde-label">Bill-To Address</label>
                <textarea className="jde-textarea" rows={3} value={po.bill_to_address || ""} onChange={(e) => set("bill_to_address", e.target.value)} data-testid="f-bill-to" />
              </div>
            </div>
            <div style={{ height: 10 }} />
            <label className="jde-label">Notes / Extraction Comments</label>
            <textarea className="jde-textarea" rows={2} value={po.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="f-notes" />
            {po.extraction_notes && (
              <div style={{ marginTop: 6, fontSize: 11, color: "var(--jde-text-muted)", fontStyle: "italic" }}>
                AI notes: {po.extraction_notes}
              </div>
            )}
          </div>
        )}

        {tab === "lines" && (
          <div>
            <table className="jde-grid lines-editable" data-testid="lines-grid" style={{ width: "100%", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ width: 30 }}>#</th>
                  <th style={{ width: 150 }}>SKU</th>
                  <th style={{ width: 55 }}>Rev</th>
                  <th>Description</th>
                  <th style={{ width: 60 }}>UOM</th>
                  <th className="num" style={{ width: 85 }}>Ordered</th>
                  <th className="num" style={{ width: 85 }}>Shipped</th>
                  <th className="num" style={{ width: 70 }}>Open</th>
                  <th className="num" style={{ width: 95 }}>Unit Price</th>
                  <th style={{ width: 130 }}>Due Date</th>
                  <th style={{ width: 44 }}></th>
                </tr>
              </thead>
              <tbody>
                {po.line_items.map((li, i) => {
                  const open = Math.max(0, (Number(li.quantity_ordered) || 0) - (Number(li.quantity_shipped) || 0));
                  const discrep = (Number(li.quantity_shipped) || 0) > (Number(li.quantity_ordered) || 0);
                  return (
                    <tr key={i} data-testid={`line-row-${i}`}>
                      <td className="muted">{i + 1}</td>
                      <td><input className="jde-input" value={li.sku || ""} onChange={(e) => setLine(i, "sku", e.target.value)} data-testid={`line-sku-${i}`} /></td>
                      <td style={{ width: 60 }}><input className="jde-input" value={li.rev || ""} onChange={(e) => setLine(i, "rev", e.target.value)} data-testid={`line-rev-${i}`} placeholder="—" /></td>
                      <td><input className="jde-input" value={li.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} /></td>
                      <td style={{ width: 70 }}><input className="jde-input" value={li.uom || ""} onChange={(e) => setLine(i, "uom", e.target.value)} /></td>
                      <td className="num" style={{ width: 90 }}>
                        <input className="jde-input num" type="number" value={li.quantity_ordered} onChange={(e) => setLine(i, "quantity_ordered", e.target.value)} />
                      </td>
                      <td className="num" style={{ width: 90 }}>
                        <input className="jde-input num" type="number" value={li.quantity_shipped} onChange={(e) => setLine(i, "quantity_shipped", e.target.value)} />
                      </td>
                      <td className="num" style={{ fontWeight: 700, color: discrep ? "var(--jde-alert)" : open === 0 ? "var(--jde-success)" : undefined }}>
                        {discrep ? `+${(Number(li.quantity_shipped) - Number(li.quantity_ordered))} OVER` : open}
                      </td>
                      <td className="num" style={{ width: 100 }}>
                        <input className="jde-input num" type="number" value={li.unit_price ?? ""} onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                      </td>
                      <td style={{ width: 120 }}>
                        <input className="jde-input" value={li.due_date || ""} onChange={(e) => setLine(i, "due_date", e.target.value)} data-testid={`line-due-${i}`} placeholder="YYYY-MM-DD" />
                      </td>
                      <td>
                        <button className="jde-btn jde-btn-sm jde-btn-danger" onClick={() => removeLine(i)} data-testid={`line-del-${i}`}>
                          <Trash size={11} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="jde-status-bar">
              <button className="jde-btn jde-btn-sm" onClick={addLine} data-testid="add-line">
                <Plus size={11} weight="bold" /> Add Line
              </button>
              <span>Lines: {po.line_items.length}</span>
            </div>
          </div>
        )}

        {tab === "shipment" && (
          <ShipmentTab po={po} onSaved={load} />
        )}

        {tab === "timeline" && (
          <div className="jde-panel-body">
            <div className="jde-timeline" data-testid="timeline">
              {events.map((ev) => (
                <div key={ev.id} className={`jde-timeline-item ${ev.event_type}`}>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>
                    {ev.event_type.toUpperCase().replace("_", " ")}
                  </div>
                  <div style={{ fontSize: 12 }}>{ev.description}</div>
                  <div className="muted" style={{ fontSize: 10 }}>
                    {(ev.timestamp || "").slice(0, 19).replace("T", " ")}
                    {ev.quantity != null ? ` • Qty: ${ev.quantity}` : ""}
                  </div>
                </div>
              ))}
              {events.length === 0 && <div className="muted">No events recorded.</div>}
            </div>
          </div>
        )}

        {tab === "source" && (
          <div className="jde-panel-body">
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontSize: 11 }} className="muted">
                Source: {po.source_type} {po.source_filename ? `• ${po.source_filename}` : ""}
                {po.source_id ? ` • email ${po.source_id.slice(-6)}` : ""}
              </div>
              <div style={{ flex: 1 }} />
              <button
                className="jde-btn jde-btn-sm"
                onClick={async () => {
                  setReextracting(true);
                  try {
                    await api.post(`/po/${id}/reextract-source`);
                    toast.success("Source re-extracted for this PO");
                    load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Re-extract failed");
                  } finally { setReextracting(false); }
                }}
                disabled={reextracting}
                data-testid="reextract-source"
                title="Re-run AI on the original email and keep only the slice belonging to this PO"
              >
                {reextracting ? "Re-extracting…" : "Re-extract this PO's source"}
              </button>
            </div>
            {siblings.length > 0 && (
              <div style={{ marginTop: 8, padding: 8,
                background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 4,
                fontSize: 11 }} data-testid="po-siblings">
                <strong>Heads up:</strong> this PO came from an email containing {siblings.length + 1} POs.
                Other POs from the same email:&nbsp;
                {siblings.map((s, i) => (
                  <span key={s.id}>
                    {i > 0 ? ", " : ""}
                    <a href={`/orders/${s.id}`}
                       onClick={(e) => { e.preventDefault(); nav(`/orders/${s.id}`); }}
                       style={{ color: "var(--jde-link)", textDecoration: "underline" }}
                       data-testid={`sibling-link-${i}`}>
                      {s.po_number || "(unnumbered)"} — {s.customer_name || "?"}
                    </a>
                  </span>
                ))}
              </div>
            )}
            <pre style={{
              background: "#F9FAFB", border: "1px solid var(--jde-border-subtle)",
              padding: 10, marginTop: 8, fontSize: 11, whiteSpace: "pre-wrap",
              maxHeight: 480, overflow: "auto", borderRadius: 2,
            }} data-testid="source-text">
              {po.raw_text || "(no source text)"}
            </pre>
          </div>
        )}
      </div>
    </Layout>
  );
}

function ShipmentTab({ po, onSaved }) {
  const s = po.shipment || {};
  const [form, setForm] = useState({
    invoice_date: s.invoice_date || "",
    ship_sail_date: s.ship_sail_date || "",
    bl_date: s.bl_date || "", bl_number: s.bl_number || "",
    container_number: s.container_number || "",
    shipping_line: s.shipping_line || "", booking_number: s.booking_number || "",
    vessel_name: s.vessel_name || "",
    port_of_loading: s.port_of_loading || "", port_of_discharge: s.port_of_discharge || "",
    eta: s.eta || "",
    actual_delivery_date: s.actual_delivery_date || "",
    last_delivery_date: s.last_delivery_date || "",
    tracking_notes: s.tracking_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      await api.put(`/po/${po.id}/shipment`, payload);
      toast.success("Shipment details saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const FF = ({ k, label, type = "text" }) => (
    <div>
      <label className="jde-label">{label}</label>
      <input className="jde-input" type={type} value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        data-testid={`ship-${k}`} />
    </div>
  );
  return (
    <div className="jde-panel-body" data-testid="shipment-form">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        <FF k="invoice_date" label="Invoice / Ex-Factory Date" type="date" />
        <FF k="ship_sail_date" label="Ship Sail Date" type="date" />
        <FF k="bl_date" label="BL Date" type="date" />
        <FF k="bl_number" label="BL Number" />
        <FF k="container_number" label="Container Number" />
        <FF k="booking_number" label="Booking Number" />
        <FF k="shipping_line" label="Shipping Line" />
        <FF k="vessel_name" label="Vessel Name" />
        <FF k="port_of_loading" label="Port of Loading" />
        <FF k="port_of_discharge" label="Port of Discharge" />
        <FF k="eta" label="ETA" type="date" />
        <FF k="actual_delivery_date" label="Actual Delivery Date" type="date" />
        <FF k="last_delivery_date" label="Last Delivery Date (OTD basis)" type="date" />
      </div>
      <div style={{ marginTop: 10 }}>
        <label className="jde-label">Tracking Notes</label>
        <textarea className="jde-textarea" rows={2} value={form.tracking_notes}
          onChange={(e) => setForm({ ...form, tracking_notes: e.target.value })} data-testid="ship-notes" />
      </div>
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <button className="jde-btn jde-btn-primary" onClick={save} disabled={busy} data-testid="ship-save">
          {busy ? "Saving…" : "Save Shipment Details"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="jde-label">{label}</label>
      <input
        className="jde-input"
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
